// @ts-nocheck
// ═══ Bianca Visualizer V2 — Canvas-First Design ═══
// Two artists, one canvas. The tool disappears.
//
// Design philosophy (Flux):
// - The canvas IS the video. Full viewport.
// - Notes live ON the painting, not in a sidebar.
// - Tap a word to select it. Speak or type and it attaches to THAT moment.
// - The tool disappears. What's left is two artists and a canvas.

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Player, type PlayerRef } from '@remotion/player'
import { StudioComposition, type CompositionProps } from './Composition'
import type { StudioProject, LyricBlock } from './types'
import { createEmptyProject, createDemoProject } from './types'
import { startWalkthrough, shouldShowWalkthrough } from './walkthrough'
import { CinematicDemoPlayer, shouldShowCinematic, consumeCinematicJustPlayed } from './CinematicDemoPlayer'
import { loadProject, listProjects, createProject, deleteProject, listNotes, addNote, updateNote, updateProjectSettings, shareProject, listActions, listFormats, startRender, startBatchRender, getRenderStatus, type CanvasNote, type ProjectListItem, type ProjectListResponse, type PersonaAction, type FormatPreset, type RenderJob } from './api'

// ═══ Canvas Page — Unified Creative Instrument ═══

export default function CanvasPage() {
  const { id: projectId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const playerRef = useRef<PlayerRef>(null)
  const canvasRef = useRef<HTMLDivElement>(null)

  // Project browser state (when no project selected)
  const [projectList, setProjectList] = useState<ProjectListResponse | null>(null)
  const [browserLoading, setBrowserLoading] = useState(!projectId)

  // Project state
  const [project, setProject] = useState<StudioProject>(createEmptyProject())
  const [loading, setLoading] = useState(!!projectId)

  // Video passthrough mode (for reviewing existing video files)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Playback
  const [currentMs, setCurrentMs] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)

  // Notes
  const [notes, setNotes] = useState<CanvasNote[]>([])
  const [composing, setComposing] = useState<{ x: number; y: number; timestampMs: number } | null>(null)
  const [composeText, setComposeText] = useState('')

  // UI state
  const [showTimeline, setShowTimeline] = useState(false)
  const [showNoteStream, setShowNoteStream] = useState(true)
  const [hoveredWord, setHoveredWord] = useState<string | null>(null)
  const [selectedBlock, setSelectedBlock] = useState<LyricBlock | null>(null)

  // Persona collaboration state
  const [personaActions, setPersonaActions] = useState<PersonaAction[]>([])
  const [composePersona, setComposePersona] = useState<string>('')  // @persona prefix in compose

  // Settings/sharing panel state
  const [showSettings, setShowSettings] = useState(false)
  const [projectVisibility, setProjectVisibility] = useState<string>('private')
  const [shareEmail, setShareEmail] = useState('')
  const [shareStatus, setShareStatus] = useState<string | null>(null)

  // Export modal state
  const [showExport, setShowExport] = useState(false)
  const [exportFormats, setExportFormats] = useState<FormatPreset[]>([])
  const [selectedFormats, setSelectedFormats] = useState<Set<string>>(new Set(['landscape']))
  const [renderJobs, setRenderJobs] = useState<RenderJob[]>([])
  const [isRendering, setIsRendering] = useState(false)

  // Cinematic demo — first-visit to Studio landing (project browser / no projectId)
  const [showCinematic, setShowCinematic] = useState(
    () => !projectId && shouldShowCinematic()
  )

  // Auto-trigger walkthrough when a project opens for the first time.
  // If the cinematic just played (sessionStorage handshake), skip the welcome
  // card — the cinematic already said "Listen." and "Your Turn."
  useEffect(() => {
    if (!projectId || loading || !shouldShowWalkthrough()) return
    const fromCinematic = consumeCinematicJustPlayed()
    const delay = fromCinematic ? 1200 : 3000
    const timer = setTimeout(() => startWalkthrough({ fromCinematic }), delay)
    return () => clearTimeout(timer)
  }, [projectId, loading])

  // Composition props (memoized)
  const compositionProps: CompositionProps = useMemo(() => ({ project }), [project])

  // ═══ Load Project ═══

  useEffect(() => {
    if (!projectId) { setLoading(false); return }
    Promise.all([
      loadProject(projectId),
      listNotes(projectId),
    ]).then(([detail, serverNotes]) => {
      const pd = detail.project_data as any
      // Detect video passthrough mode (Video Collaborator projects)
      if (pd?.video_url) {
        setVideoUrl(pd.video_url)
        // Migrate VC notes into canvas note format
        if (pd.notes && Array.isArray(pd.notes)) {
          const migrated: CanvasNote[] = pd.notes.map((n: any, i: number) => ({
            id: `vc-note-${i}`,
            projectId: projectId || '',
            timestampMs: (n.seconds || 0) * 1000,
            text: n.text || '',
            author: 'human',
            authorLabel: 'Review',
            position: { x: 0.1, y: 0.1 + (i * 0.05) },
            status: 'open' as const,
            createdAt: new Date().toISOString(),
          }))
          setNotes([...serverNotes, ...migrated])
        } else {
          setNotes(serverNotes)
        }
      } else if (pd) {
        setProject(pd as StudioProject)
        if (pd.audio?.durationMs) setDuration(pd.audio.durationMs)
        setNotes(serverNotes)
      }
      if (detail.visibility) setProjectVisibility(detail.visibility)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [projectId])

  // ═══ Load Project Browser (when no project selected) ═══

  useEffect(() => {
    if (projectId) return
    setBrowserLoading(true)
    listProjects().then(data => {
      setProjectList(data)
      setBrowserLoading(false)
    }).catch(() => setBrowserLoading(false))
  }, [projectId])

  // Poll for new notes from collaborators every 10s
  useEffect(() => {
    if (!projectId) return
    const interval = setInterval(() => {
      listNotes(projectId).then(serverNotes => {
        setNotes(prev => {
          // Merge: keep server notes, preserve any local-only notes not yet synced
          const serverIds = new Set(serverNotes.map(n => n.id))
          const localOnly = prev.filter(n => !serverIds.has(n.id))
          return [...serverNotes, ...localOnly]
        })
      }).catch(() => {})
      // Also poll persona actions
      listActions(projectId).then(setPersonaActions).catch(() => {})
    }, 10000)
    return () => clearInterval(interval)
  }, [projectId])

  // Load export formats on mount
  useEffect(() => {
    listFormats().then(setExportFormats).catch(() => {})
  }, [])

  // ═══ Playback Tracking ═══

  useEffect(() => {
    if (videoUrl) {
      // Video passthrough — track via video element
      const interval = setInterval(() => {
        const v = videoRef.current
        if (!v) return
        setCurrentMs(Math.round(v.currentTime * 1000))
        if (v.duration && !isNaN(v.duration)) setDuration(Math.round(v.duration * 1000))
      }, 50)
      return () => clearInterval(interval)
    }
    // Remotion mode
    const interval = setInterval(() => {
      const player = playerRef.current
      if (!player) return
      const frame = (player as any).getCurrentFrame?.()
      if (typeof frame === 'number') {
        setCurrentMs(Math.round((frame / (project.visuals.fps || 30)) * 1000))
      }
    }, 50)
    return () => clearInterval(interval)
  }, [project.visuals.fps, videoUrl])

  // ═══ Canvas Click → New Note ═══

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (composing) return // Already composing
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return

    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height

    // Check if clicking near an active lyric word
    const activeLyric = project.lyrics.find(b =>
      currentMs >= b.startMs && currentMs < b.endMs
    )

    setComposing({ x, y, timestampMs: currentMs })
    setSelectedBlock(activeLyric || null)

    // Pause while composing
    if (isPlaying) {
      playerRef.current?.pause()
      setIsPlaying(false)
    }
  }, [composing, currentMs, project.lyrics, isPlaying])

  // ═══ Submit Note ═══

  const submitNote = useCallback(async () => {
    if (!composeText.trim() || !composing) return

    const tempId = `note-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    const noteData = {
      timestampMs: composing.timestampMs,
      text: composeText.trim(),
      authorLabel: 'You',
      position: { x: composing.x, y: composing.y },
      targetWord: selectedBlock?.text,
      targetBlockId: selectedBlock?.id,
    }

    // Optimistic update
    const tempNote: CanvasNote = {
      id: tempId,
      projectId: projectId || '',
      ...noteData,
      author: 'human',
      authorLabel: 'You',
      position: { x: composing.x, y: composing.y },
      status: 'open',
      createdAt: new Date().toISOString(),
    }
    setNotes(prev => [...prev, tempNote])
    setComposeText('')
    setComposing(null)
    setSelectedBlock(null)

    // Persist to server
    if (projectId) {
      try {
        const result = await addNote(projectId, noteData)
        // Replace temp ID with server ID
        setNotes(prev => prev.map(n => n.id === tempId ? { ...n, id: result.id } : n))
      } catch { /* keep optimistic note */ }
    }
  }, [composeText, composing, selectedBlock, projectId])

  // ═══ Keyboard Shortcuts ═══

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (composing) {
        if (e.key === 'Escape') {
          setComposing(null)
          setComposeText('')
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          submitNote()
        }
        return
      }
      if (e.key === ' ') {
        e.preventDefault()
        if (videoUrl) {
          const v = videoRef.current
          if (v) { if (v.paused) { v.play(); setIsPlaying(true) } else { v.pause(); setIsPlaying(false) } }
        } else {
          if (isPlaying) { playerRef.current?.pause(); setIsPlaying(false) }
          else { playerRef.current?.play(); setIsPlaying(true) }
        }
      }
      if (e.key === 't') setShowTimeline(prev => !prev)
      if (e.key === 'n') setShowNoteStream(prev => !prev)
      if (e.key === 'e') setShowExport(prev => !prev)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [composing, isPlaying, submitNote])

  // ═══ Format Time ═══

  const formatTime = (ms: number) => {
    const s = Math.floor(ms / 1000)
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m}:${String(sec).padStart(2, '0')}`
  }

  // ═══ Notes visible at current time ═══

  const visibleNotes = notes.filter(n =>
    Math.abs(n.timestampMs - currentMs) < 5000 // Show notes within 5s of current position
  )

  // ═══ Active lyric ═══

  const activeLyric = project.lyrics.find(b =>
    currentMs >= b.startMs && currentMs < b.endMs
  )

  // ═══ Project Browser (No Project Selected) ═══

  if (!projectId) {
    const renderProjectCard = (p: ProjectListItem, badge?: string) => (
      <div
        key={p.id}
        onClick={() => navigate(`/tech/studio/${p.id}`)}
        style={{
          padding: '16px 20px', background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
          cursor: 'pointer', transition: 'all 0.15s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,220,80,0.3)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#eee', flex: 1 }}>{p.title || 'Untitled'}</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {badge && <span style={{ fontSize: 10, color: '#888', background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: 4 }}>{badge}</span>}
            <button
              onClick={async (e) => {
                e.stopPropagation()
                if (!confirm('Archive this project?')) return
                try {
                  await deleteProject(p.id)
                  setProjectList(prev => prev ? {
                    owned: prev.owned.filter(x => x.id !== p.id),
                    shared: prev.shared.filter(x => x.id !== p.id),
                    public: prev.public.filter(x => x.id !== p.id),
                  } : null)
                } catch {}
              }}
              title="Archive project"
              style={{
                padding: '2px 6px', fontSize: 10, border: 'none', borderRadius: 4,
                background: 'transparent', color: '#555', cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#f66'; e.currentTarget.style.background = 'rgba(255,80,80,0.1)' }}
              onMouseLeave={e => { e.currentTarget.style.color = '#555'; e.currentTarget.style.background = 'transparent' }}
            >
              ✕
            </button>
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
          {new Date(p.updated_at || p.created_at).toLocaleDateString()}
        </div>
      </div>
    )

    return (
      <>
        {showCinematic && (
          <CinematicDemoPlayer
            audioUrl="/studio/cinematic_v0.1_draft.mp3"
            onComplete={() => setShowCinematic(false)}
            onSkip={() => setShowCinematic(false)}
          />
        )}
        <div style={{
          position: 'fixed', inset: 0, background: '#0a0a0f',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'auto',
        }}>
        <div style={{ maxWidth: 520, width: '90%', padding: '40px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 40, opacity: 0.3 }}>🎬</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#eee', marginTop: 8 }}>Creative Studio</div>
            <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Two artists, one canvas. The tool disappears.</div>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 28 }}>
            <button
              disabled={browserLoading}
              onClick={async () => {
                setBrowserLoading(true)
                try {
                  const result = await createProject('Untitled Project')
                  navigate(`/tech/studio/${result.id}`)
                } catch { setBrowserLoading(false) }
              }}
              style={{
                padding: '10px 24px', fontSize: 13, fontWeight: 500,
                background: 'rgba(255,220,80,0.15)', color: '#FFDC50',
                border: '1px solid rgba(255,220,80,0.3)', borderRadius: 8,
                cursor: browserLoading ? 'wait' : 'pointer', opacity: browserLoading ? 0.5 : 1,
              }}
            >
              + New Project
            </button>
            <button
              disabled={browserLoading}
              onClick={async () => {
                setBrowserLoading(true)
                try {
                  const demo = createDemoProject()
                  const result = await createProject('Welcome to the Studio', demo)
                  navigate(`/tech/studio/${result.id}`)
                } catch { setBrowserLoading(false) }
              }}
              style={{
                padding: '10px 24px', fontSize: 13, fontWeight: 500,
                background: 'rgba(180,140,255,0.12)', color: '#B48CFF',
                border: '1px solid rgba(180,140,255,0.25)', borderRadius: 8,
                cursor: browserLoading ? 'wait' : 'pointer', opacity: browserLoading ? 0.5 : 1,
              }}
            >
              {browserLoading ? 'Creating...' : 'Try the Demo'}
            </button>
          </div>

          {browserLoading && (
            <div style={{ textAlign: 'center', color: '#666', fontSize: 13 }}>Loading projects...</div>
          )}

          {projectList && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {projectList.owned.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Your Projects</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {projectList.owned.map(p => renderProjectCard(p))}
                  </div>
                </div>
              )}
              {projectList.shared.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Shared With You</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {projectList.shared.map(p => renderProjectCard(p, 'shared'))}
                  </div>
                </div>
              )}
              {projectList.public.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                    Community Gallery
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {projectList.public.map(p => (
                      <div
                        key={p.id}
                        onClick={() => navigate(`/tech/studio/${p.id}`)}
                        style={{
                          padding: '14px 16px', background: 'rgba(180,140,255,0.06)',
                          border: '1px solid rgba(180,140,255,0.12)', borderRadius: 10,
                          cursor: 'pointer', transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(180,140,255,0.12)'; e.currentTarget.style.borderColor = 'rgba(180,140,255,0.3)' }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(180,140,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(180,140,255,0.12)' }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#eee' }}>{p.title || 'Untitled'}</div>
                        <div style={{ fontSize: 10, color: '#777', marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                          <span>🌐 public</span>
                          <span>{new Date(p.updated_at || p.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {projectList.owned.length === 0 && projectList.shared.length === 0 && projectList.public.length === 0 && (
                <div style={{ textAlign: 'center', color: '#666', fontSize: 13, marginTop: 16, lineHeight: 1.8 }}>
                  No projects yet.<br />
                  <span style={{ color: '#B48CFF' }}>Try the Demo</span> to explore the instrument,
                  or create a new project to start fresh.
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </>
    )
  }

  // ═══ Loading State ═══

  if (loading) {
    return (
      <div style={{
        position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0a0a0f', color: '#888', fontSize: 14,
      }}>
        Loading canvas...
      </div>
    )
  }

  // Duration from audio, or from lyrics/sections if no audio (e.g. demo project)
  const lyricEndMs = project.lyrics.length > 0 ? Math.max(...project.lyrics.map(l => l.endMs)) : 0
  const sectionEndMs = project.sections.length > 0 ? Math.max(...project.sections.map(s => s.endMs)) : 0
  const contentDurationMs = Math.max(lyricEndMs, sectionEndMs)
  const durationMs = project.audio?.durationMs || duration || contentDurationMs || 1
  const fps = project.visuals.fps || 30
  const totalFrames = Math.round((durationMs / 1000) * fps)

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', overflow: 'hidden' }}>

      {/* ═══ THE CANVAS — Full Viewport ═══ */}
      <div
        ref={canvasRef}
        data-tour="canvas"
        onClick={handleCanvasClick}
        style={{
          position: 'absolute', inset: 0,
          cursor: composing ? 'default' : 'crosshair',
        }}
      >
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        ) : (
          <Player
            ref={playerRef}
            component={StudioComposition}
            inputProps={compositionProps}
            durationInFrames={Math.max(1, totalFrames)}
            fps={fps}
          compositionWidth={project.visuals.width || 1920}
          compositionHeight={project.visuals.height || 1080}
          style={{ width: '100%', height: '100%' }}
          controls={false}
          clickToPlay={false}
          numberOfSharedAudioTags={2}
        />
        )}
      </div>

      {/* ═══ Empty Project Guidance ═══ */}
      {!videoUrl && project.lyrics.length === 0 && !project.audio && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 15, pointerEvents: 'none',
        }}>
          <div style={{ textAlign: 'center', pointerEvents: 'auto' }}>
            <div style={{ fontSize: 48, opacity: 0.2, marginBottom: 12 }}>🎵</div>
            <div style={{ fontSize: 18, fontWeight: 500, color: 'rgba(255,255,255,0.5)' }}>
              Empty canvas
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', marginTop: 8, maxWidth: 280, lineHeight: 1.6 }}>
              Open the <strong>Editor</strong> to import audio and lyrics,<br />
              or click anywhere to leave a note.
            </div>
          </div>
        </div>
      )}

      {/* ═══ Floating Notes ON the Canvas ═══ */}
      {visibleNotes.map(note => {
        const age = Math.abs(note.timestampMs - currentMs)
        const opacity = age < 2000 ? 1 : 1 - ((age - 2000) / 3000)
        const isPersona = note.author !== 'human' && note.author !== 'joel'
        const isRequest = note.text.startsWith('@')
        const pendingAction = personaActions.find(a => a.note_id === note.id && a.status === 'pending')
        return (
          <div
            key={note.id}
            style={{
              position: 'absolute',
              left: `${note.position.x * 100}%`,
              top: `${note.position.y * 100}%`,
              transform: 'translate(-50%, -100%)',
              maxWidth: 240,
              padding: '8px 12px',
              background: isPersona
                ? 'rgba(120, 180, 255, 0.92)'
                : isRequest
                  ? 'rgba(180, 140, 255, 0.92)'
                  : 'rgba(255, 220, 80, 0.92)',
              color: '#111',
              borderRadius: 6,
              fontSize: 13,
              lineHeight: 1.4,
              opacity: Math.max(0, opacity),
              transition: 'opacity 0.3s ease',
              pointerEvents: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
              zIndex: 30,
              borderLeft: isPersona ? '3px solid #78B4FF' : isRequest ? '3px solid #B48CFF' : 'none',
            }}
          >
            {note.targetWord && (
              <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 2 }}>
                re: "{note.targetWord}"
              </div>
            )}
            <div>{note.text}</div>
            <div style={{ fontSize: 10, opacity: 0.5, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              {note.authorLabel} · {formatTime(note.timestampMs)}
              {pendingAction && <span style={{ color: '#B48CFF', fontWeight: 600 }}> · awaiting response</span>}
              {note.status === 'resolved' && <span style={{ color: '#4a4' }}> · ✓</span>}
            </div>
          </div>
        )
      })}

      {/* ═══ Compose Note (Post-it Input) ═══ */}
      {composing && (
        <div
          style={{
            position: 'absolute',
            left: `${composing.x * 100}%`,
            top: `${composing.y * 100}%`,
            transform: 'translate(-50%, -100%)',
            width: 260,
            padding: 12,
            background: 'rgba(255, 220, 80, 0.95)',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            zIndex: 50,
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ fontSize: 10, color: '#555', marginBottom: 6 }}>
            Note at {formatTime(composing.timestampMs)}
            {selectedBlock && <span> · on "{selectedBlock.text.slice(0, 30)}"</span>}
          </div>
          <textarea
            autoFocus
            value={composeText}
            onChange={e => setComposeText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitNote() }
              if (e.key === 'Escape') { setComposing(null); setComposeText('') }
            }}
            placeholder="What should change here?"
            style={{
              width: '100%', minHeight: 60, border: 'none', outline: 'none',
              background: 'transparent', resize: 'vertical', fontSize: 13,
              fontFamily: 'inherit', color: '#111',
            }}
          />
          {/* Persona request hint */}
          {composeText.startsWith('@') && (
            <div style={{ fontSize: 10, color: '#777', marginBottom: 4 }}>
              Requesting persona action — e.g. @flux adjust the color palette here
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, marginTop: 6 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {['flux', 'timbre', 'forge'].map(p => (
                <button
                  key={p}
                  onClick={() => setComposeText(`@${p} `)}
                  title={`Request action from ${p}`}
                  style={{
                    padding: '2px 6px', fontSize: 10, border: 'none', borderRadius: 3,
                    background: composeText.startsWith(`@${p}`) ? 'rgba(120,180,255,0.4)' : 'rgba(0,0,0,0.08)',
                    color: composeText.startsWith(`@${p}`) ? '#fff' : '#666', cursor: 'pointer',
                  }}
                >
                  @{p}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => { setComposing(null); setComposeText('') }}
                style={{
                  padding: '4px 10px', fontSize: 11, border: 'none', borderRadius: 4,
                  background: 'rgba(0,0,0,0.1)', cursor: 'pointer', color: '#555',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitNote}
                style={{
                  padding: '4px 10px', fontSize: 11, border: 'none', borderRadius: 4,
                  background: '#333', color: '#fff', cursor: 'pointer',
                }}
              >
                {composeText.startsWith('@') ? 'Request' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Note Stream (Floating Panel — Right Edge) ═══ */}
      {showNoteStream && notes.length > 0 && (
        <div style={{
          position: 'absolute', right: 16, top: 16, bottom: showTimeline ? 80 : 60,
          width: 220, overflow: 'auto',
          background: 'rgba(0, 0, 0, 0.6)', borderRadius: 10,
          backdropFilter: 'blur(12px)', padding: 10,
          zIndex: 20,
        }}>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
            Notes ({notes.length})
          </div>
          {[...notes].reverse().map(note => {
            const isPersona = note.author !== 'human' && note.author !== 'joel'
            const isRequest = note.text.startsWith('@')
            const action = personaActions.find(a => a.note_id === note.id)
            return (
              <div
                key={note.id}
                onClick={() => {
                  const frame = Math.round((note.timestampMs / 1000) * fps)
                  playerRef.current?.seekTo(frame)
                  setCurrentMs(note.timestampMs)
                }}
                onDoubleClick={() => {
                  const newStatus = note.status === 'resolved' ? 'open' : 'resolved'
                  setNotes(prev => prev.map(n => n.id === note.id ? { ...n, status: newStatus } : n))
                  updateNote(note.id, { status: newStatus }).catch(() => {})
                }}
                style={{
                  padding: '6px 8px', marginBottom: 6, borderRadius: 6, cursor: 'pointer',
                  background: note.status === 'resolved'
                    ? 'rgba(80, 200, 80, 0.15)'
                    : isPersona
                      ? 'rgba(120, 180, 255, 0.15)'
                      : isRequest
                        ? 'rgba(180, 140, 255, 0.15)'
                        : 'rgba(255, 220, 80, 0.15)',
                  fontSize: 12, color: '#ddd',
                  borderLeft: `3px solid ${isPersona ? '#78B4FF' : isRequest ? '#B48CFF' : '#FFDC50'}`,
                }}
              >
                <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>
                  {note.authorLabel} · {formatTime(note.timestampMs)}
                  {note.status === 'resolved' && ' ✓'}
                  {action?.status === 'pending' && ' ⏳'}
                  {action?.status === 'completed' && ' ✓ responded'}
                </div>
                <div style={{ lineHeight: 1.3 }}>{note.text}</div>
              </div>
            )
          })}
        </div>
      )}

      {/* ═══ Minimal Floating Controls (Bottom Center) ═══ */}
      <div style={{
        position: 'absolute', bottom: showTimeline ? 72 : 16, left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 16px', borderRadius: 24,
        background: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(12px)',
        zIndex: 40,
      }}>
        {/* Back link */}
        <Link
          to="/tech/studio"
          style={{ color: '#888', fontSize: 12, textDecoration: 'none' }}
          title="Back to projects"
        >
          ←
        </Link>

        {/* Play/Pause */}
        <button
          data-tour="play-btn"
          onClick={() => {
            if (videoUrl) {
              const v = videoRef.current
              if (!v) return
              if (isPlaying) { v.pause(); setIsPlaying(false) }
              else { v.play(); setIsPlaying(true) }
            } else {
              if (isPlaying) { playerRef.current?.pause(); setIsPlaying(false) }
              else { playerRef.current?.play(); setIsPlaying(true) }
            }
          }}
          style={{
            width: 36, height: 36, borderRadius: '50%', border: 'none',
            background: 'rgba(255,255,255,0.15)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 16,
          }}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>

        {/* Time */}
        <span style={{ color: '#ccc', fontSize: 12, fontFamily: 'monospace', minWidth: 80 }}>
          {formatTime(currentMs)} / {formatTime(durationMs)}
        </span>

        {/* Scrubber */}
        <input
          data-tour="scrubber"
          type="range"
          min={0}
          max={durationMs}
          value={currentMs}
          onChange={e => {
            const ms = Number(e.target.value)
            if (videoUrl) {
              const v = videoRef.current
              if (v) v.currentTime = ms / 1000
            } else {
              const frame = Math.round((ms / 1000) * fps)
              playerRef.current?.seekTo(frame)
            }
            setCurrentMs(ms)
          }}
          style={{ width: 200, accentColor: '#FFDC50' }}
        />

        {/* Toggle buttons */}
        <button
          data-tour="notes-btn"
          onClick={() => setShowNoteStream(prev => !prev)}
          title="Toggle notes (N)"
          style={{
            padding: '4px 8px', fontSize: 11, border: 'none', borderRadius: 4,
            background: showNoteStream ? 'rgba(255,220,80,0.3)' : 'rgba(255,255,255,0.1)',
            color: showNoteStream ? '#FFDC50' : '#888', cursor: 'pointer',
          }}
        >
          Notes
        </button>
        <button
          data-tour="timeline-btn"
          onClick={() => setShowTimeline(prev => !prev)}
          title="Toggle timeline (T)"
          style={{
            padding: '4px 8px', fontSize: 11, border: 'none', borderRadius: 4,
            background: showTimeline ? 'rgba(120,180,255,0.3)' : 'rgba(255,255,255,0.1)',
            color: showTimeline ? '#78B4FF' : '#888', cursor: 'pointer',
          }}
        >
          Timeline
        </button>
        <Link
          to={`/tech/studio/${projectId}/editor`}
          data-tour="editor-btn"
          title="Open full timeline editor"
          style={{
            padding: '4px 8px', fontSize: 11, borderRadius: 4, textDecoration: 'none',
            background: 'rgba(255,255,255,0.1)', color: '#888',
          }}
        >
          Editor
        </Link>
        <button
          onClick={() => setShowExport(true)}
          data-tour="export-btn"
          title="Export video"
          style={{
            padding: '4px 8px', fontSize: 11, border: 'none', borderRadius: 4,
            background: 'rgba(80,200,80,0.2)', color: '#6d6', cursor: 'pointer',
          }}
        >
          Export
        </button>
        <button
          onClick={() => setShowSettings(prev => !prev)}
          data-tour="settings-btn"
          title="Project settings & sharing"
          style={{
            padding: '4px 8px', fontSize: 11, border: 'none', borderRadius: 4,
            background: showSettings ? 'rgba(180,140,255,0.3)' : 'rgba(255,255,255,0.1)',
            color: showSettings ? '#B48CFF' : '#888', cursor: 'pointer',
          }}
        >
          ⚙
        </button>
        <button
          onClick={() => startWalkthrough()}
          title="Take a guided tour"
          style={{
            padding: '4px 8px', fontSize: 11, border: 'none', borderRadius: 4,
            background: 'rgba(255,255,255,0.1)', color: '#888', cursor: 'pointer',
          }}
        >
          ?
        </button>
      </div>

      {/* ═══ Timeline Peek (Expandable Strip at Bottom) ═══ */}
      {showTimeline && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 56,
          background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          zIndex: 35, display: 'flex', alignItems: 'center', padding: '0 16px',
        }}>
          {/* Section blocks */}
          <div style={{ flex: 1, position: 'relative', height: 32, borderRadius: 4, overflow: 'hidden' }}>
            {/* Background */}
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.05)' }} />

            {/* Sections */}
            {project.sections.map(section => {
              const left = (section.startMs / durationMs) * 100
              const width = ((section.endMs - section.startMs) / durationMs) * 100
              return (
                <div
                  key={section.id}
                  style={{
                    position: 'absolute',
                    left: `${left}%`, width: `${width}%`,
                    top: 0, bottom: 0,
                    background: section.backgroundColor + '40',
                    borderRight: '1px solid rgba(255,255,255,0.1)',
                  }}
                  title={section.name}
                >
                  <span style={{
                    position: 'absolute', top: 2, left: 4,
                    fontSize: 9, color: 'rgba(255,255,255,0.5)',
                  }}>
                    {section.name}
                  </span>
                </div>
              )
            })}

            {/* Note markers */}
            {notes.map(note => (
              <div
                key={note.id}
                onClick={() => {
                  const frame = Math.round((note.timestampMs / 1000) * fps)
                  playerRef.current?.seekTo(frame)
                  setCurrentMs(note.timestampMs)
                }}
                style={{
                  position: 'absolute',
                  left: `${(note.timestampMs / durationMs) * 100}%`,
                  top: 20, width: 6, height: 6,
                  borderRadius: '50%',
                  background: note.author === 'human' ? '#FFDC50' : '#78B4FF',
                  transform: 'translateX(-50%)',
                  cursor: 'pointer',
                  zIndex: 2,
                }}
                title={`${note.authorLabel}: ${note.text}`}
              />
            ))}

            {/* Playhead */}
            <div style={{
              position: 'absolute',
              left: `${(currentMs / durationMs) * 100}%`,
              top: 0, bottom: 0, width: 2,
              background: '#ff4444', zIndex: 3,
            }} />
          </div>
        </div>
      )}

      {/* ═══ Active Lyric Overlay (Tap-to-Select Words) ═══ */}
      {activeLyric && !composing && (
        <div style={{
          position: 'absolute', bottom: showTimeline ? 140 : 80,
          left: '50%', transform: 'translateX(-50%)',
          padding: '8px 16px', borderRadius: 10,
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,220,80,0.15)',
          zIndex: 25, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center',
          maxWidth: '85%',
        }}>
          <div style={{
            width: '100%', textAlign: 'center', fontSize: 10,
            color: 'rgba(255,220,80,0.5)', marginBottom: 2,
          }}>
            tap a word to annotate
          </div>
          {activeLyric.text.split(/\s+/).map((word, i) => {
            const isHovered = hoveredWord === `${activeLyric.id}-${i}`
            const wordNotes = notes.filter(n =>
              n.targetWord === word && Math.abs(n.timestampMs - currentMs) < 5000
            )
            return (
              <span
                key={i}
                onClick={e => {
                  e.stopPropagation()
                  const rect = canvasRef.current?.getBoundingClientRect()
                  if (!rect) return
                  setComposing({
                    x: (e.clientX - rect.left) / rect.width,
                    y: (e.clientY - rect.top) / rect.height,
                    timestampMs: currentMs,
                  })
                  setSelectedBlock({ ...activeLyric, text: word } as any)
                }}
                onMouseEnter={() => setHoveredWord(`${activeLyric.id}-${i}`)}
                onMouseLeave={() => setHoveredWord(null)}
                style={{
                  cursor: 'pointer', fontSize: 15, color: '#fff',
                  padding: '3px 6px', borderRadius: 4, position: 'relative',
                  background: isHovered
                    ? 'rgba(255, 220, 80, 0.35)'
                    : wordNotes.length > 0
                      ? 'rgba(255, 220, 80, 0.12)'
                      : 'transparent',
                  borderBottom: wordNotes.length > 0 ? '2px solid rgba(255,220,80,0.5)' : 'none',
                  transition: 'background 0.15s ease',
                  fontWeight: isHovered ? 600 : 400,
                }}
              >
                {word}
                {wordNotes.length > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -4,
                    width: 12, height: 12, borderRadius: '50%',
                    background: '#FFDC50', color: '#111',
                    fontSize: 8, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {wordNotes.length}
                  </span>
                )}
              </span>
            )
          })}
        </div>
      )}

      {/* ═══ Settings & Sharing Panel ═══ */}
      {showSettings && (
        <div
          style={{
            position: 'absolute', top: 16, left: 16, width: 300,
            background: 'rgba(10, 10, 20, 0.92)', backdropFilter: 'blur(16px)',
            borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)',
            padding: 20, zIndex: 50,
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: '#eee', marginBottom: 16 }}>
            Project Settings
          </div>

          {/* Visibility */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              Visibility
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['private', 'shared', 'public'] as const).map(vis => (
                <button
                  key={vis}
                  onClick={async () => {
                    setProjectVisibility(vis)
                    if (projectId) {
                      await updateProjectSettings(projectId, { visibility: vis }).catch(() => {})
                    }
                  }}
                  style={{
                    flex: 1, padding: '6px 0', fontSize: 11, border: 'none', borderRadius: 6,
                    background: projectVisibility === vis ? 'rgba(180,140,255,0.3)' : 'rgba(255,255,255,0.06)',
                    color: projectVisibility === vis ? '#B48CFF' : '#888',
                    cursor: 'pointer', textTransform: 'capitalize',
                  }}
                >
                  {vis === 'private' ? '🔒 Private' : vis === 'shared' ? '👥 Shared' : '🌐 Public'}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 10, color: '#555', marginTop: 6 }}>
              {projectVisibility === 'private' && 'Only you can see this project.'}
              {projectVisibility === 'shared' && 'Visible to people you share with.'}
              {projectVisibility === 'public' && 'Visible in the community gallery.'}
            </div>
          </div>

          {/* Share with someone */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              Share With
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                placeholder="User email or ID"
                value={shareEmail}
                onChange={e => { setShareEmail(e.target.value); setShareStatus(null) }}
                style={{
                  flex: 1, padding: '6px 10px', fontSize: 12, border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6, background: 'rgba(255,255,255,0.04)', color: '#ddd',
                  outline: 'none',
                }}
              />
              <button
                onClick={async () => {
                  if (!shareEmail.trim() || !projectId) return
                  try {
                    await shareProject(projectId, shareEmail.trim(), 'editor')
                    setShareStatus('Shared!')
                    setShareEmail('')
                    setTimeout(() => setShareStatus(null), 2000)
                  } catch {
                    setShareStatus('Failed')
                  }
                }}
                style={{
                  padding: '6px 12px', fontSize: 11, border: 'none', borderRadius: 6,
                  background: 'rgba(255,220,80,0.2)', color: '#FFDC50', cursor: 'pointer',
                }}
              >
                Share
              </button>
            </div>
            {shareStatus && (
              <div style={{ fontSize: 10, color: shareStatus === 'Shared!' ? '#6d6' : '#f66', marginTop: 4 }}>
                {shareStatus}
              </div>
            )}
          </div>

          <button
            onClick={() => setShowSettings(false)}
            style={{
              width: '100%', padding: '6px 0', fontSize: 11, border: 'none', borderRadius: 6,
              background: 'rgba(255,255,255,0.06)', color: '#888', cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      )}

      {/* ═══ Project Title (Top Left, Subtle) ═══ */}
      <div style={{
        position: 'absolute', top: 12, left: 16,
        color: 'rgba(255,255,255,0.4)', fontSize: 13,
        zIndex: 20, pointerEvents: 'none',
      }}>
        {project.title}
      </div>

      {/* ═══ Keyboard Hints (Top Right, Fades) ═══ */}
      <div style={{
        position: 'absolute', top: 12, right: showNoteStream && notes.length > 0 ? 250 : 16,
        color: 'rgba(255,255,255,0.25)', fontSize: 10,
        zIndex: 20, pointerEvents: 'none',
        transition: 'right 0.3s ease',
      }}>
        Space: play/pause · Click: note · T: timeline · N: notes · E: export
      </div>

      {/* ═══ Pending Persona Actions Indicator ═══ */}
      {personaActions.filter(a => a.status === 'pending').length > 0 && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          padding: '4px 12px', borderRadius: 12,
          background: 'rgba(180, 140, 255, 0.2)', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(180, 140, 255, 0.3)',
          fontSize: 11, color: '#B48CFF', zIndex: 20,
        }}>
          ⏳ {personaActions.filter(a => a.status === 'pending').length} persona request{personaActions.filter(a => a.status === 'pending').length > 1 ? 's' : ''} pending
        </div>
      )}

      {/* ═══ Export Modal ═══ */}
      {showExport && (
        <div
          onClick={() => !isRendering && setShowExport(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 400, padding: 24, borderRadius: 12,
              background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 600, color: '#eee', marginBottom: 16 }}>
              Export Video
            </div>

            {/* Format Selection */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Formats
              </div>
              {(exportFormats.length > 0 ? exportFormats : [
                { key: 'landscape', width: 1920, height: 1080, label: 'Landscape 16:9' },
                { key: 'vertical', width: 1080, height: 1920, label: 'Vertical 9:16' },
                { key: 'square', width: 1080, height: 1080, label: 'Square 1:1' },
                { key: 'hd', width: 1280, height: 720, label: 'HD 720p' },
              ]).map(fmt => (
                <label
                  key={fmt.key}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    borderRadius: 6, cursor: 'pointer', marginBottom: 4,
                    background: selectedFormats.has(fmt.key) ? 'rgba(80,200,80,0.1)' : 'transparent',
                    border: `1px solid ${selectedFormats.has(fmt.key) ? 'rgba(80,200,80,0.3)' : 'rgba(255,255,255,0.06)'}`,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedFormats.has(fmt.key)}
                    onChange={() => {
                      setSelectedFormats(prev => {
                        const next = new Set(prev)
                        if (next.has(fmt.key)) next.delete(fmt.key)
                        else next.add(fmt.key)
                        return next
                      })
                    }}
                    style={{ accentColor: '#6d6' }}
                  />
                  <div>
                    <div style={{ fontSize: 13, color: '#eee' }}>{fmt.label}</div>
                    <div style={{ fontSize: 10, color: '#666' }}>{fmt.width}×{fmt.height}</div>
                  </div>
                </label>
              ))}
            </div>

            {/* Render Jobs Status */}
            {renderJobs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                  Render Progress
                </div>
                {renderJobs.map(job => (
                  <div key={job.id} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: '#ccc' }}>{job.formatLabel}</span>
                      <span style={{
                        color: job.status === 'completed' ? '#6d6'
                          : job.status === 'failed' ? '#f66'
                          : '#FFDC50'
                      }}>
                        {job.status === 'completed' ? '✓ Done' : job.status === 'failed' ? '✗ Failed' : `${job.progress}%`}
                      </span>
                    </div>
                    {job.status === 'rendering' && (
                      <div style={{ marginTop: 4, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.1)' }}>
                        <div style={{
                          height: '100%', borderRadius: 2, background: '#FFDC50',
                          width: `${job.progress}%`, transition: 'width 0.3s ease',
                        }} />
                      </div>
                    )}
                    {job.status === 'completed' && job.outputUrl && (
                      <a
                        href={job.outputUrl}
                        download
                        style={{ fontSize: 11, color: '#78B4FF', textDecoration: 'none', display: 'block', marginTop: 2 }}
                      >
                        ↓ Download
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => { setShowExport(false); setRenderJobs([]) }}
                style={{
                  padding: '8px 16px', fontSize: 12, border: 'none', borderRadius: 6,
                  background: 'rgba(255,255,255,0.08)', color: '#888', cursor: 'pointer',
                }}
              >
                {isRendering ? 'Close' : 'Cancel'}
              </button>
              {!isRendering && (
                <button
                  onClick={async () => {
                    if (selectedFormats.size === 0) return
                    setIsRendering(true)
                    try {
                      const formats = Array.from(selectedFormats)
                      if (formats.length === 1) {
                        const result = await startRender(project, formats[0])
                        setRenderJobs([{ id: result.jobId, status: 'queued', progress: 0, format: formats[0], formatLabel: (exportFormats.find(f => f.key === formats[0])?.label || formats[0]), width: 0, height: 0, outputUrl: null, error: null, startedAt: new Date().toISOString() }])
                        // Poll for status
                        const pollInterval = setInterval(async () => {
                          try {
                            const status = await getRenderStatus(result.jobId)
                            setRenderJobs([status])
                            if (status.status === 'completed' || status.status === 'failed') {
                              clearInterval(pollInterval)
                              setIsRendering(false)
                            }
                          } catch { clearInterval(pollInterval); setIsRendering(false) }
                        }, 2000)
                      } else {
                        const result = await startBatchRender(project, formats)
                        setRenderJobs(result.jobs.map(j => ({
                          id: j.jobId, status: 'queued' as const, progress: 0,
                          format: j.format, formatLabel: j.label,
                          width: 0, height: 0, outputUrl: null, error: null,
                          startedAt: new Date().toISOString(), batchId: result.batchId,
                        })))
                        // Poll all jobs
                        const pollInterval = setInterval(async () => {
                          try {
                            const statuses = await Promise.all(result.jobs.map(j => getRenderStatus(j.jobId)))
                            setRenderJobs(statuses)
                            if (statuses.every(s => s.status === 'completed' || s.status === 'failed')) {
                              clearInterval(pollInterval)
                              setIsRendering(false)
                            }
                          } catch { clearInterval(pollInterval); setIsRendering(false) }
                        }, 2000)
                      }
                    } catch (err) {
                      setIsRendering(false)
                    }
                  }}
                  disabled={selectedFormats.size === 0}
                  style={{
                    padding: '8px 20px', fontSize: 12, fontWeight: 500, border: 'none', borderRadius: 6,
                    background: selectedFormats.size > 0 ? 'rgba(80,200,80,0.3)' : 'rgba(255,255,255,0.05)',
                    color: selectedFormats.size > 0 ? '#6d6' : '#555', cursor: selectedFormats.size > 0 ? 'pointer' : 'default',
                  }}
                >
                  Render {selectedFormats.size > 1 ? `${selectedFormats.size} formats` : '1 format'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
